import { ConnectButton } from '@rainbow-me/rainbowkit'
import Logo from '../assets/logo.svg'
import {
  Box,
  Flex,
  Text,
  IconButton,
  Stack,
  Collapse,
  Icon,
  Link,
  Popover,
  PopoverTrigger,
  PopoverContent,
  useColorModeValue,
  useBreakpointValue,
  useDisclosure,
  useColorMode,
} from '@chakra-ui/react'
import {
  HamburgerIcon,
  CloseIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  SunIcon,
  MoonIcon,
} from '@chakra-ui/icons'

export default function Header() {
  const { isOpen, onToggle } = useDisclosure()
  const { colorMode, toggleColorMode } = useColorMode()
  return (
    <Box minH={'10'} className={'sticky'}>
      <Flex
        bg={useColorModeValue('white', 'gray.800')}
        color={useColorModeValue('gray.600', 'white')}
        minH={'60px'}
        py={{ base: 2 }}
        px={{ base: 4 }}
        borderBottom={1}
        borderStyle={'solid'}
        borderColor={useColorModeValue('gray.200', 'gray.900')}
        align={'center'}
      >
        <Flex
          flex={{ base: 1, md: 'auto' }}
          ml={{ base: -2 }}
          display={{ base: 'flex', md: 'none' }}
        >
          <IconButton
            onClick={onToggle}
            icon={isOpen ? <CloseIcon w={3} h={3} /> : <HamburgerIcon w={5} h={5} />}
            variant={'ghost'}
            aria-label={'Toggle Navigation'}
          />
        </Flex>
        <Flex flex={{ base: 2 }} justify={{ base: 'center', md: 'start' }}>
          <Text
            textAlign={useBreakpointValue({ base: 'center', md: 'left' })}
            color={useColorModeValue('gray.800', 'white')}
          >
            <a href={'/'}>
              <img src={Logo} alt="subtle_web3" />
            </a>
          </Text>

          <Flex display={{ base: 'none', md: 'flex' }} ml={10}>
            <DesktopNav />
          </Flex>
        </Flex>

        <Stack flex={{ base: 1, md: 2 }} justify={'flex-end'} direction={'row'} spacing={8}>
          <ConnectButton label="Connect" />
          {/* <IconButton
            onClick={toggleColorMode}
            icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
            aria-label={'Toggle Mode'}
          /> */}
        </Stack>
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        <MobileNav />
      </Collapse>
    </Box>
  )
}

const DesktopNav = () => {
  const linkColor = useColorModeValue('gray.600', 'gray.200')
  const linkHoverColor = useColorModeValue('gray.800', 'white')
  const popoverContentBgColor = useColorModeValue('white', 'gray.800')

  return (
    <Stack direction={'row'} spacing={8} minH={{ base: '5vh', md: '7vh', lg: '5vh' }}>
      {NAV_ITEMS.map((navItem) => (
        <Box key={navItem.label} alignItems={'center'} justifyItems={'center'}>
          <Popover trigger={'hover'} placement={'bottom-start'}>
            <PopoverTrigger>
              <Link
                href={navItem.href ?? '#'}
                fontSize={'md'}
                fontWeight={500}
                color={linkColor}
                _hover={{
                  textDecoration: 'none',
                  color: linkHoverColor,
                }}
                lineHeight={'10'}
              >
                {navItem.label}
              </Link>
            </PopoverTrigger>

            {navItem.children && (
              <PopoverContent
                border={0}
                boxShadow={'xl'}
                bg={popoverContentBgColor}
                p={4}
                rounded={'xl'}
                minW={'sm'}
              >
                <Stack>
                  {navItem.children.map((child) => (
                    <DesktopSubNav key={child.label} {...child} />
                  ))}
                </Stack>
              </PopoverContent>
            )}
          </Popover>
        </Box>
      ))}
    </Stack>
  )
}

const DesktopSubNav = ({ label, href, subLabel }: NavItem) => {
  return (
    <Link
      href={href}
      role={'group'}
      display={'block'}
      p={2}
      rounded={'md'}
      _hover={{ bg: useColorModeValue('brand.50', 'gray.900') }}
    >
      <Stack direction={'row'} align={'center'}>
        <Box>
          <Text transition={'all .3s ease'} _groupHover={{ color: 'brand.400' }} fontWeight={500}>
            {label}
          </Text>
          <Text fontSize={'sm'}>{subLabel}</Text>
        </Box>
        <Flex
          transition={'all .3s ease'}
          transform={'translateX(-10px)'}
          opacity={0}
          _groupHover={{ opacity: '100%', transform: 'translateX(0)' }}
          justify={'flex-end'}
          align={'center'}
          flex={1}
        >
          <Icon color={'pink.400'} w={5} h={5} as={ChevronRightIcon} />
        </Flex>
      </Stack>
    </Link>
  )
}

const MobileNav = () => {
  return (
    <Stack bg={useColorModeValue('white', 'gray.800')} p={4} display={{ md: 'none' }}>
      {NAV_ITEMS.map((navItem) => (
        <MobileNavItem key={navItem.label} {...navItem} />
      ))}
    </Stack>
  )
}

const MobileNavItem = ({ label, children, href }: NavItem) => {
  const { isOpen, onToggle } = useDisclosure()

  return (
    <Stack spacing={4} onClick={children && onToggle}>
      <Flex
        py={2}
        as={Link}
        href={href ?? '#'}
        justify={'space-between'}
        align={'center'}
        _hover={{
          textDecoration: 'none',
        }}
      >
        <Text fontWeight={600} color={useColorModeValue('gray.600', 'gray.200')}>
          {label}
        </Text>
        {children && (
          <Icon
            as={ChevronDownIcon}
            transition={'all .25s ease-in-out'}
            transform={isOpen ? 'rotate(180deg)' : ''}
            w={6}
            h={6}
          />
        )}
      </Flex>

      <Collapse in={isOpen} animateOpacity style={{ marginTop: '0!important' }}>
        <Stack
          mt={2}
          pl={4}
          borderLeft={1}
          borderStyle={'solid'}
          borderColor={useColorModeValue('gray.200', 'gray.700')}
          align={'start'}
        >
          {children &&
            children.map((child) => (
              <Link key={child.label} py={2} href={child.href}>
                {child.label}
              </Link>
            ))}
        </Stack>
      </Collapse>
    </Stack>
  )
}

interface NavItem {
  label: string
  subLabel?: string
  children?: Array<NavItem>
  href?: string
}

const NAV_ITEMS: Array<NavItem> = [
  {
    label: 'Basic',
    children: [
      {
        label: 'Transfer Tokens 💸',
        subLabel: 'Transfer Matic Tokens to any Address on the Mumbai Testnet',
        href: '/tokentransfer',
      },
      {
        label: 'Bill Board 🖼️',
        subLabel: 'Store and Retrieve Data from the Blockchain',
        href: '/simplestorage',
      },
      {
        label: 'Task Manager ✅',
        subLabel: 'Create, Update and Manage Tasks with Smart Contract',
        href: '/taskmanage',
      },
      {
        label: 'Poll in the Wall 🧱',
        subLabel: 'Hassle-free File Uploads to IPFS Decentralized Storage Platform',
        href: '/poll',
      },
      {
        label: 'Web3 Cloud ☁️',
        subLabel: 'Upload Files to IPFS',
        href: '/ipfs-upload',
      },
    ],
  },
  {
    label: 'Intermediate',
    children: [
      {
        label: 'Rock Paper Scissors ✊✋✌️',
        subLabel: 'Fair and Decentralized Rock Paper Scissors on Blockchain',
        href: '/rps',
      },
      // {
      //   label: 'Subtle Faces NFT Collection',
      //   subLabel: 'Exculsive NFT ready for Mint!',
      //   href: '/subtlefaces',
      // },
      {
        label: 'Tic Tac Toe ✖️⭕',
        subLabel: 'Tic Tac Toe for all the Web3 Enthusiasts',
        href: '/tictactoe',
      },
      {
        label: 'Inscriptions 🙉',
        subLabel: 'Create your NFT inscription on Mumbai Testnet',
        href: '/inscription',
      },
      {
        label: 'Split_Pay ✂️',
        subLabel: 'Decentralised Payment Splitter for every project',
        href: '/split-pay',
      },
    ],
  },
  {
    label: 'Generative_AI',
    children: [
      {
        label: 'PlotGen.AI  🤖',
        subLabel: 'Describe in few words to generate a movie plot & more',
        href: '/plotgen',
      },
    ],
  },
  {
    label: 'Web3_Tools',
    children: [
      {
        label: 'Geth Cmds 🖥️',
        subLabel: 'Geth Commands for Private Blockchain creation',
        href: '/geth-commands',
      },
      {
        label: 'Valid_Eth ✔️',
        subLabel: 'Filters Valid Eth Addresses from Others',
        href: '/valid-eth',
      },
      {
        label: 'Count My NFTs 🔢',
        subLabel: 'Find the right trait size for your NFT collection',
        href: '/count-my-nft',
      },
    ],
  },
  {
    label: 'About',
    href: '/about',
  },
  // {
  //   label: 'Hire Designers',
  //   href: '#',
  // },
]
